import React from 'react';
import { Plus, X } from 'lucide-react';
import { OPERATORS } from './conditions';

const inputClass = "w-full px-3 py-2 border border-[#C9C9C9] rounded text-[13px] text-[#181818] focus:outline-none focus:border-[#0176D3] focus:ring-1 focus:ring-[#0176D3] transition-all";
const labelClass = "text-[12px] font-semibold text-[#5C5C5C] block mb-1.5";

/**
 * Rule-builder for one component's or one page's `condition`. Shared by
 * PropertiesPanel (fields) and FormBuilderPage (pages) — the shape is
 * identical, only what it's attached to differs. `fields` is the list of
 * other fields this condition is allowed to reference (see
 * conditions.js#referenceableFields).
 */
export default function ConditionEditor({ condition, fields, onChange, title = 'Conditional Logic', emptyLabel = 'this item' }) {
  const current = condition || { enabled: false, match: 'all', rules: [] };

  const set = (patch) => onChange({ ...current, ...patch });

  const updateRule = (index, patch) => {
    const rules = [...current.rules];
    rules[index] = { ...rules[index], ...patch };
    set({ rules });
  };
  const addRule = () => {
    set({ rules: [...current.rules, { fieldId: fields[0]?.id || '', operator: 'equals', value: '' }] });
  };
  const removeRule = (index) => {
    set({ rules: current.rules.filter((_, i) => i !== index) });
  };

  return (
    <div className="pt-4 border-t border-[#F0F1F3] space-y-3">
      <label className="flex items-center gap-2 text-[13px] font-medium text-[#181818] cursor-pointer">
        <input
          type="checkbox"
          checked={!!current.enabled}
          onChange={(e) => set({ enabled: e.target.checked })}
          className="accent-[#0176D3] w-4 h-4"
          disabled={!fields.length}
        />
        {title}
      </label>

      {!fields.length && (
        <p className="text-[11px] text-[#706E6B]">Add another field first — conditions reference other fields' answers.</p>
      )}

      {current.enabled && fields.length > 0 && (
        <div className="space-y-2.5 pl-1">
          <p className="text-[11px] text-[#706E6B]">
            Show {emptyLabel} only when
            {current.rules.length > 1 && (
              <>
                {' '}
                <select
                  className="inline-block px-1 py-0.5 border border-[#D8DDE6] rounded-[4px] text-[11px] font-bold text-[#0176D3]"
                  value={current.match || 'all'}
                  onChange={(e) => set({ match: e.target.value })}
                >
                  <option value="all">all</option>
                  <option value="any">any</option>
                </select>
              </>
            )}
            {' '}of these are true:
          </p>

          {current.rules.map((rule, i) => {
            const op = OPERATORS.find((o) => o.value === rule.operator) || OPERATORS[0];
            return (
              <div key={i} className="flex items-center gap-1.5">
                <select
                  className={`${inputClass} flex-1`}
                  value={rule.fieldId}
                  onChange={(e) => updateRule(i, { fieldId: e.target.value })}
                >
                  {fields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
                <select
                  className={`${inputClass} flex-1`}
                  value={rule.operator}
                  onChange={(e) => updateRule(i, { operator: e.target.value })}
                >
                  {OPERATORS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {op.needsValue && (
                  <input
                    className={`${inputClass} flex-1`}
                    value={rule.value ?? ''}
                    onChange={(e) => updateRule(i, { value: e.target.value })}
                    placeholder="Value"
                  />
                )}
                <button onClick={() => removeRule(i)} className="p-2 text-[#706E6B] hover:text-red-500 shrink-0">
                  <X size={14} />
                </button>
              </div>
            );
          })}

          <button
            onClick={addRule}
            className="flex items-center gap-1.5 text-[12px] font-bold text-[#0176D3] hover:text-[#015BA0]"
          >
            <Plus size={14} /> Add Condition
          </button>
        </div>
      )}
    </div>
  );
}
